import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActionTypeEnum,
  AcTransactionSearchModel,
  AcTransactionTypeEnum,
  HasFormIdModel,
  InputDatePickerTimeDisplayModeEnum,
  LookupItemModel,
  RequestActionModel,
  SearchAcTransactionRequestModel,
} from "../../models";
import {
  generateGuid,
  getLabelName,
  getLookUpItemValue,
  getPagePrivileges,
  isArabicCurrentLanguage,
} from "../../utils";
import { searchAcTransactionTreasury } from "../../serviceBroker/acTransactionsApiServiceBroker";
import {
  ButtonBox,
  GridTable,
  InputDatePicker,
  LoadingBox,
  PrivilegesChecker,
  SelectBox,
  TextBox,
} from "..";

interface AcTransactionListProps extends HasFormIdModel {
  acTransactionType: AcTransactionTypeEnum;
  accountLookups: LookupItemModel[];
  userLookUps: LookupItemModel[];
  setIsRefresh: Dispatch<SetStateAction<boolean>>;
  isRefresh: boolean;
  onActionEvent: (o: RequestActionModel) => void;
}

export const AcTransactionList: FC<AcTransactionListProps> = ({
  acTransactionType,
  accountLookups,
  userLookUps,
  setIsRefresh,
  isRefresh,
  onActionEvent = () => {},
  formId,
}) => {
  //#region variables
  const defaultPageSize: number = 50;
  const initialValues: SearchAcTransactionRequestModel = {
    pageNumber: 1,
    pageSize: defaultPageSize,
    acTransactionType: +acTransactionType,
  };
  //#endregion
  //#region state
  const [searchParams, setSearchParams] =
    useState<SearchAcTransactionRequestModel>(initialValues);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AcTransactionSearchModel[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const accountsSelectBoxMultiselectRef = useRef<any>();
  const usersSelectBoxMultiselectRef = useRef<any>();
  //#endregion
  //#region useEffect
  useEffect(() => {
    const fillData = async () => {
      setLoading(true);
      await getTransactions();
      setLoading(false);
    };
    fillData().then(() => {});
  }, [searchParams.pageNumber, searchParams.pageSize, searchParams.searchGuid]);
  useEffect(() => {
    if (isRefresh) {
      const fillData = async () => {
        setLoading(true);
        await getTransactions();
        setLoading(false);
      };
      fillData().then(() => {});
      setIsRefresh(false);
    }
  }, [isRefresh]);
  //#endregion
  //#region functions
  const getTransactions = async () => {
    const result = await searchAcTransactionTreasury(searchParams);
    setData(result?.Result || []);
    setTotalRows(result?.TotalRecoredCount || 0);
  };
  const onCurrentPageChange = async (pageNumber: number) => {
    if (pageNumber !== searchParams.pageNumber) {
      const obj = { ...searchParams };
      obj.pageNumber = pageNumber;
      setSearchParams(obj);
    }
  };
  const onPageSizeChange = async (pageSize: number) => {
    if (pageSize !== searchParams.pageSize) {
      const obj = { ...searchParams };
      obj.pageSize = pageSize;
      setSearchParams(obj);
    }
  };
  //#endregion
  //#region component
  const subHeaderComponent = useMemo(() => {
    return (
      <>
        <div className="row g-3 mb-2">
          <div className="col-md-4 col-sm-12">
            <SelectBox
              labelName={getLabelName("Account")}
              isSingleSelect={true}
              selectedValues={
                searchParams.accountId
                  ? [searchParams.accountId.toString()]
                  : [""]
              }
              source={accountLookups}
              onStatusChange={(e: LookupItemModel) => {
                const val = getLookUpItemValue(e);
                setSearchParams({
                  ...searchParams,
                  accountId: val === null ? null : Number(val),
                });
              }}
              multiselectRef={accountsSelectBoxMultiselectRef}
            />
          </div>
          <div className="col-md-4 col-sm-12">
            <SelectBox
              labelName={getLabelName("User")}
              isSingleSelect={true}
              selectedValues={
                searchParams.userId ? [searchParams.userId.toString()] : [""]
              }
              source={userLookUps}
              onStatusChange={(e: LookupItemModel) => {
                const val = getLookUpItemValue(e);
                setSearchParams({
                  ...searchParams,
                  userId: val === null ? null : Number(val),
                });
              }}
              multiselectRef={usersSelectBoxMultiselectRef}
            />
          </div>
          <div className="col-md-4 col-sm-12">
            <TextBox
              labelName={getLabelName("code")}
              key={searchParams.transactionNumber}
              inputName={"transactionNumber"}
              inputValue={searchParams.transactionNumber}
              onChange={(e: any) => {
                setSearchParams({
                  ...searchParams,
                  transactionNumber: e.target.value,
                });
              }}
            />
          </div>
          <div className="col-md-4 col-sm-12">
            <InputDatePicker
              selectedDate={searchParams.fromDate}
              isClearable
              InputLabel={"from Date"}
              selectsEnd
              timeMode={InputDatePickerTimeDisplayModeEnum.TimeSelect}
              startDate={searchParams.fromDate}
              className="form-control"
              // minDate={searchParams.fromDate}
              endDate={searchParams.toDate}
              onChange={(date) => {
                setSearchParams({
                  ...searchParams,
                  fromDate: date === null ? undefined : date,
                });
              }}
            />
          </div>
          <div className="col-md-4 col-sm-12">
            <InputDatePicker
              selectedDate={searchParams.toDate}
              isClearable
              InputLabel={"to Date"}
              timeMode={InputDatePickerTimeDisplayModeEnum.TimeSelect}
              selectsEnd
              startDate={searchParams.fromDate}
              className="form-control"
              minDate={searchParams.fromDate}
              endDate={searchParams.toDate}
              onChange={(date) => {
                setSearchParams({
                  ...searchParams,
                  toDate: date === null ? undefined : date,
                });
              }}
            />
          </div>
          <div className="col-md-4 col-sm-12 d-flex justify-content-end">
            <PrivilegesChecker formId={formId} action="EnableSearch">
              <ButtonBox
                iconType="account-search"
                className="btn-sm mx-2 btn-gradient-primary"
                onClick={() => {
                  setSearchParams({
                    ...searchParams,
                    searchGuid: generateGuid(),
                  });
                  //onActionEvent({request: null, action: ActionTypeEnum.Search})
                }}
              >
                {getLabelName("Search")}
              </ButtonBox>
            </PrivilegesChecker>
            <ButtonBox
              iconType="receipt"
              variant="danger"
              className="btn btn-sm"
              onClick={() => {
                accountsSelectBoxMultiselectRef.current.clearValue();
                usersSelectBoxMultiselectRef.current.clearValue();
                setSearchParams({
                  ...initialValues,
                  searchGuid: generateGuid(),
                });
              }}
            >
              {getLabelName("Cancel")}
            </ButtonBox>
          </div>
        </div>
      </>
    );
  }, [searchParams, accountLookups, userLookUps]);
  //#endregion
  //#region html

  return (
    <>
      {loading && <LoadingBox />}
      {getPagePrivileges().EnableSearch && (
        <PrivilegesChecker formId={formId} action="EnableSearch">
          {subHeaderComponent}
        </PrivilegesChecker>
      )}

      <GridTable
        //columns={columns}
        columnsProps={{
          actions: [
            ActionTypeEnum.Update,
            ActionTypeEnum.Delete,
            ActionTypeEnum.PrintPdf,
          ],
          isArabic: isArabicCurrentLanguage(),
          onActionEvent,
        }}
        data={data}
        externalLoading={loading}
        totalRows={totalRows}
        formId={formId}
        currentPage={searchParams.pageNumber || 1}
        pageSize={searchParams.pageSize || defaultPageSize}
        onCurrentPageChange={onCurrentPageChange}
        onPageSizeChange={onPageSizeChange}
        paginationType="server"
      />
    </>
  );
  //#endregion
};
